import { getCityListByCountryCode, getCountryListV2 } from 'apis/home';
import { LANGUAGE_MAP, LanguageType } from 'constant/language';
import { getShopConfig, SHOP_MAP } from 'constant/shop';
import dayjs from 'dayjs';
import getContractPrintList from './get-contract-print-list';

// 合同的文案语言
export type ContractLang = Extract<LanguageType, 'en' | 'ja' | 'fr' | 'zh_CN'>;

// 合同的数据语言，对于法国来说，只能展示 en
export type ContractDataLang = Extract<LanguageType, 'en' | 'ja' | 'zh_CN'>;

/**
 * 合同类型 4种 分别是
 * 回收合同（客户存根）
 * 回收合同（店铺存根）
 * 寄卖合同（客户存根）
 * 寄卖合同（店铺存根）
 */
export type ContractType =
  'RECYCLE_CUSTOMER' | 'RECYCLE_STORE' | 'SELL_CUSTOMER' | 'SELL_STORE';

export type ContractPrintProduct = {
  price: number;
  brandName: string;
  id: number;
  name: string;
  guestRemark: string;
};

export type ContractStyleType = 'black' | 'white';

const getDate = ({
  agreeFinalValuationTime,
  createTime,
}: {
  agreeFinalValuationTime?: string;
  createTime: string;
}): string => {
  return agreeFinalValuationTime
    ? dayjs(agreeFinalValuationTime).format('YYYY/MM/DD')
    : dayjs(createTime).format('YYYY/MM/DD');
};

const getStaffName = ({
  staffName,
  createBy,
  staffSelectOptions,
}: {
  staffSelectOptions: { label: string; value: number }[];
  staffName?: string;
  createBy?: number;
}): string => {
  if (staffName?.trim()) {
    return staffName.trim();
  }

  if (createBy) {
    return (
      staffSelectOptions.find((option) => option.value === createBy)?.label ??
      ''
    );
  }

  return '-';
};

const getStoreName = ({ storeId }: { storeId: number }): string => {
  return getShopConfig(storeId).companyName;
};

const getContractLang = (storeId: number): ContractLang => {
  let contractLang = getShopConfig(storeId).language;

  if (storeId === SHOP_MAP.FRANCE) {
    contractLang = LANGUAGE_MAP.FR;
  }

  if (
    contractLang !== LANGUAGE_MAP.EN &&
    contractLang !== LANGUAGE_MAP.JA &&
    contractLang !== LANGUAGE_MAP.FR &&
    contractLang !== LANGUAGE_MAP.ZH_CN
  ) {
    contractLang = LANGUAGE_MAP.EN;
  }

  return contractLang;
};

const getContractDataLang = (storeId: number): ContractDataLang => {
  let contractLang = getShopConfig(storeId).language;

  if (
    contractLang !== LANGUAGE_MAP.EN &&
    contractLang !== LANGUAGE_MAP.JA &&
    contractLang !== LANGUAGE_MAP.ZH_CN
  ) {
    contractLang = LANGUAGE_MAP.EN;
  }

  return contractLang;
};

const getSettlementType = ({
  isTradeIn,
  contractLang,
  settlementType,
}: {
  isTradeIn: number;
  contractLang: ContractLang;
  settlementType: 1 | 2 | null;
}): string => {
  // 置换
  if (isTradeIn === 1) {
    return {
      [LANGUAGE_MAP.EN]: 'Trade-In',
      [LANGUAGE_MAP.JA]: '下取り',
      [LANGUAGE_MAP.ZH_CN]: '置换',
      [LANGUAGE_MAP.FR]: 'Reprise',
    }[contractLang];
  }

  // 现金
  if (settlementType === 1) {
    return {
      [LANGUAGE_MAP.EN]: 'CASH TRANSFER',
      [LANGUAGE_MAP.JA]: '現金',
      [LANGUAGE_MAP.ZH_CN]: '现金',
      [LANGUAGE_MAP.FR]: 'Espèces',
    }[contractLang];
  }

  // 银行转账
  if (settlementType === 2) {
    return {
      [LANGUAGE_MAP.EN]: 'BANK TRANSFER',
      [LANGUAGE_MAP.JA]: '振込',
      [LANGUAGE_MAP.ZH_CN]: '转账',
      [LANGUAGE_MAP.FR]: 'Virement bancaire',
    }[contractLang];
  }

  return '';
};

const validateOrderList = (
  orderList: {
    type: number;
    submissionId: number | null;
  }[],
): boolean => {
  if (orderList.length === 0) {
    return false;
  }

  // type 必须全部相等
  if (orderList.some((order) => order.type !== orderList[0].type)) {
    return false;
  }

  const submissionId = orderList[0].submissionId;
  if (submissionId === null) {
    return false;
  }

  // submissionId 必须全部相等且非空
  if (orderList.some((order) => order.submissionId !== submissionId)) {
    return false;
  }

  return true;
};

const getUserInfoRenderLineList = async ({
  phone,
  phone2,
  storeId,
  idCertificate,
  name,
  country,
  city,
  detailAddress,
  postCode,
  payoutDetail,
}: {
  phone: string;
  phone2: string;
  storeId: number;
  idCertificate: string;
  name: string;
  country: string;
  city: string;
  detailAddress: string;
  postCode: string;
  payoutDetail: string;
}): Promise<string[]> => {
  const lineList: string[] = [];
  const countryIsJP = country === 'JPN';
  const contractDataLang = getContractDataLang(storeId);

  // 姓名
  const nameStr = countryIsJP ? `${name || '-'}  様` : `${name || '-'}`;
  lineList.push(nameStr);

  // 电话 & 证件号
  const phoneInfo = (() => {
    const phones = [phone, phone2].map((p) => p.trim()).filter(Boolean);
    if (phones.length === 0) return '';

    const idCertificateStr = idCertificate.trim() || '';
    const phoneStr = phones.map((p) => `+${p}`).join('  ');
    if (idCertificateStr && storeId === SHOP_MAP.HONGKONG) {
      return `${phoneStr}        ${idCertificateStr}`;
    }

    return phoneStr;
  })();
  lineList.push(phoneInfo);

  // 地址
  const countryList = await getCountryListV2();
  const countryOption = countryList.find((d) => d.code === country);
  const countryLabel = (() => {
    if (!countryOption) return '';
    if (countryIsJP) return '';

    const nameI18n = countryOption.nameI18n;
    if (!nameI18n) return countryOption.name || '';

    return (
      nameI18n[contractDataLang] || nameI18n['en'] || countryOption.name || ''
    );
  })();
  let cityStr = '';
  if (country) {
    const cityData = await getCityListByCountryCode(country, contractDataLang);
    cityStr = cityData.data.cityList?.find((d) => d.code === city)?.name || '';
  }
  const addressInfo = [countryLabel, cityStr, detailAddress, postCode]
    .filter(Boolean)
    .join(' ');
  lineList.push(addressInfo);

  // 支付详情
  const isSgStore =
    storeId === SHOP_MAP.SINGAPORE ||
    storeId === SHOP_MAP.SINGAPORE_ASU_GST ||
    storeId === SHOP_MAP.SINGAPORE_GX;
  if (payoutDetail.trim() && isSgStore) {
    payoutDetail
      .split('\n')
      .map((d) => d.trim())
      .filter(Boolean)
      .forEach((d) => lineList.push(d));
  }

  return lineList.map((line) => line.trim()).filter(Boolean);
};

const contractPrintUtils = {
  getDate,
  getStaffName,
  getStoreName,
  getContractLang,
  getContractDataLang,
  getSettlementType,
  validateOrderList,
  getContractPrintList,
  getUserInfoRenderLineList,
};

export default contractPrintUtils;
